import { parseAddress } from 'addresser';
import { compareDesc } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CdkDrag } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';

import { SnCamera } from '@lib/api/entities';
import { snSlideInUp, snSlideOutDown } from '@lib/common/animations';
import { SnLocationFieldsGeneratorService } from '@lib/locations/services/location-fields-generator.service';
import { SnDataListComponent } from '@lib/ui/data-list';
import { SnFormFieldConfig } from 'libs/form/src/lib/types/form-field-config.type';

import { FsWhoosterAddressModel } from '../../models/whooster-address.model';
import { FsWhoosterHistoryModel } from '../../models/whooster-history.model';
import { FsWhoosterPhoneModel } from '../../models/whooster-phone.model';
import { SnWhoosterService } from '../../services/whooster-service.service';

@Component({
  selector: 'sn-whooster-dialog',
  templateUrl: './whooster-dialog.component.html',
  styleUrls: ['./whooster-dialog.component.scss'],
  animations: [snSlideInUp, snSlideOutDown],
})
export class FsWhoosterDialogComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnCamera>;

  @ViewChild('tabs', { static: false }) public tabs: MatTabGroup;

  @ViewChild(CdkDrag)
  public readonly drag: CdkDrag;
  public isMinimized: boolean = false;
  public isLoading: boolean = false;
  public invalidAddress: string;
  public search: string = null;
  public addressParam: any;
  public phoneParam: any;
  public searchParams: any;
  public searchResponse: any;
  public addressArray: FsWhoosterAddressModel[] = [];
  public phoneArray: FsWhoosterPhoneModel[] = [];
  public searchHistory: FsWhoosterHistoryModel[] = [];
  public whoosterTimeout: any;
  public selectedIndex = 0;
  public createdAddress: string;
  public searchId: string;

  public readonly addressColumns = ['name', 'dob', 'phone', 'email', 'date', 'pdf'];

  public readonly phoneColumns = ['name', 'dob', 'email', 'address', 'pdf'];

  public readonly historyColumns = ['searchparameter'];

  public form = new FormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [
    {
      fieldGroup: [
        this._locationFieldsGenerator.getAddressField({
          templateOptions: {
            floatLabel: 'always',
            change: (field, value) => {
              if (value) {
                this.createdAddress = value.formattedAddress;
              }
            },
          },
        }),
      ],
    },
  ];

  private readonly _destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      address: string;
      phone: string;
    },
    private readonly _cd: ChangeDetectorRef,
    private readonly _whoosterService: SnWhoosterService,
    private readonly _dialogRef: MatDialogRef<FsWhoosterDialogComponent>,
    private readonly _locationFieldsGenerator: SnLocationFieldsGeneratorService,
  ) {}

  get invalidParams(): boolean {
    return !this.addressParam && !this.phoneParam;
  }

  public ngOnInit(): void {
    this.searchItem();
    this.formModel.address = this.data.address;
  }

  public ngOnDestroy(): void {
    clearTimeout(this.whoosterTimeout);
    this._destroy$.next();
    this._destroy$.complete();
  }

  public searchParam(item: any): void {
    this.back();
    this.data.address = null;
    this.data.phone = null;
    this.addressParam = null;
    this.phoneParam = null;
    item?.searchType === 'address'
      ? (this.data.address = item.searchParameter)
      : (this.data.phone = item.searchParameter);
    this.searchItem();
    this.tabs.selectedIndex = 0;
  }

  public searchItem(): void {
    if (this.data?.address) {
      try {
        const parsed = parseAddress(this.data.address);
        if (
          parsed &&
          parsed.addressLine1 &&
          parsed.placeName &&
          parsed.stateName &&
          parsed.zipCode
        ) {
          this.addressParam = {
            searchParams: {
              address: parsed.addressLine1,
              city: parsed.placeName,
              state: parsed.stateName,
              zip: parsed.zipCode,
              limit: '10',
            },
            product_name: 'per-pch',
          };
        }
      } catch (error) {
        this.invalidAddress = error;
      }
    }
    if (this.data?.phone) {
      this.phoneParam = {
        searchParams: {
          phoneNumber: this.data.phone,
          limit: '10',
        },
        product_name: 'tel-ap',
      };
    }
    if (!this.invalidAddress && this.addressParam && !this.phoneParam) {
      this.addressSearch();
    }
    if (!this.invalidAddress && !this.addressParam && this.phoneParam) {
      this.phoneSearch();
    }
    this.whoosterHistory();
  }

  public whoosterHistory(): void {
    this._whoosterService
      .getWhoosterHistory()
      .pipe(takeUntil(this._destroy$))
      .subscribe((results) => {
        this.searchHistory = null;
        this.searchHistory = results.results;
      });
  }

  public whoosterPdf(item: String): void {
    this._whoosterService.whoosterPdf(this.searchId).pipe(takeUntil(this._destroy$)).subscribe();
  }

  public addressValidSearch(): void {
    this.search = 'address';
    this.isLoading = true;
    try {
      const parsed = parseAddress(this.createdAddress);
      if (parsed && parsed.addressLine1 && parsed.placeName && parsed.stateName && parsed.zipCode) {
        this.addressParam = {
          searchParams: {
            address: parsed.addressLine1,
            city: parsed.placeName,
            state: parsed.stateName,
            zip: parsed.zipCode,
            limit: '10',
          },
          product_name: 'per-pch',
        };
        this._whoosterService
          .createWhooster(this.addressParam)
          .pipe(takeUntil(this._destroy$))
          .subscribe((result) => {
            this.getWhoosterAddress(result.id);
          });
      }
    } catch (error) {
      this.invalidAddress = error;
    }
  }

  public addressSearch(): void {
    this.search = 'address';
    this.isLoading = true;
    this._whoosterService
      .createWhooster(this.addressParam)
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this.searchId = result.id;
        this.getWhoosterAddress(result.id);
      });
  }

  public phoneSearch(): void {
    this.search = 'phone';
    this.isLoading = true;
    this._whoosterService
      .createWhooster(this.phoneParam)
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this.searchId = result.id;
        this.getWhoosterPhone(result.id);
      });
  }

  public isArrays(item: any): boolean {
    return Array.isArray(item);
  }

  public sortResult(result: any): any {
    return result.sort((a: any, b: any) => {
      return compareDesc(new Date(`1/${a.endDate}`), new Date(`1/${b.endDate}`));
    });
  }

  public getWhoosterAddress(id: any): void {
    setTimeout(() => {
      this._whoosterService
        .getWhoosterAddress(id)
        .pipe(takeUntil(this._destroy$))
        .subscribe((result) => {
          if (String(result.results.status) !== 'completed') {
            this.getWhoosterAddress(id);
          }
          if (String(result.results.status) === 'completed') {
            this.isLoading = false;
            this.searchResponse = result.results.searchResponse;
            this.addressArray =
              result?.addressArray?.length > 0
                ? this.sortResult(result?.addressArray)
                : result?.addressArray;
          }
        });
    }, 5000);
  }

  public getWhoosterPhone(id: any): void {
    setTimeout(() => {
      this._whoosterService
        .getWhoosterPhone(id)
        .pipe(takeUntil(this._destroy$))
        .subscribe((result) => {
          if (String(result.results.status) !== 'completed') {
            this.getWhoosterPhone(id);
          }
          if (String(result.results.status) === 'completed') {
            this.isLoading = false;
            this.searchResponse = result.results.searchResponse;
            this.phoneArray =
              result?.phoneArray?.length > 0
                ? this.sortResult(result?.phoneArray)
                : result?.phoneArray;
          }
        });
    }, 5000);
  }

  public back(): void {
    this.search = null;
    this.isLoading = false;
    this.searchResponse = null;
    this.phoneArray = [];
    clearTimeout(this.whoosterTimeout);
  }

  public toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    const cdkContainer: any = document.querySelector('.map__whooster-dialog');
    if (this.isMinimized) {
      this._dialogRef.updateSize('470px', '60px');
      cdkContainer.classList.add('sn__whooster-dialog');
    } else {
      this._dialogRef.updateSize('40vw', 'initial');
      cdkContainer.classList.remove('sn__whooster-dialog');
    }
    this.drag.reset();
    this._cd.detectChanges();
  }
}
